import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@crisellevaldez/speculo-ui';
import { notifyCredentialConnected, notifyCredentialDeleted } from '../../api/rollout';
import { updateOrganization } from '../../api/remiApi';
import { Organization } from '../../types/organization.types';

interface UseRolloutCrmConnectionOptions {
  /** The selected organization */
  selectedOrg?: Organization;
  /** Callback when connection/disconnection succeeds */
  onSuccess?: () => void;
}

interface CredentialPayload {
  id: string;
  appKey: string;
  subdomain?: string;
}

/**
 * Converts appKey to a normalized key for storage
 * e.g., 'follow-up-boss' -> 'fub', 'sierra-interactive' -> 'sierra'
 */
const normalizeAppKey = (appKey: string): string => {
  const keyMap: Record<string, string> = {
    'follow-up-boss': 'fub',
    'sierra-interactive': 'sierra',
    'cinc': 'cinc',
    'cinc-pro': 'cinc',
    'brivity': 'brivity',
    'kvcore': 'brivity',
  };
  return keyMap[appKey] || appKey.replace(/-/g, '_');
};

/**
 * Converts appKey to a human-readable name for toast messages
 */
const getAppDisplayName = (appKey: string): string => {
  const nameMap: Record<string, string> = {
    'follow-up-boss': 'Follow Up Boss',
    'sierra-interactive': 'Sierra Interactive',
    'cinc': 'CINC',
    'cinc-pro': 'CINC',
    'brivity': 'Brivity',
    'kvcore': 'Brivity',
  };
  return nameMap[appKey] || appKey.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Hook for managing CRM connection state via Rollout
 * Handles connecting, disconnecting, and persisting state via Remi API
 * Supports any CRM dynamically based on appKey
 *
 * @example
 * const { handleCredentialAdded, handleCredentialDeleted, isConnecting, isDisconnecting } = useRolloutCrmConnection({
 *   selectedOrg,
 *   onSuccess: () => refetch(),
 * });
 */
export const useRolloutCrmConnection = ({
  selectedOrg,
  onSuccess,
}: UseRolloutCrmConnectionOptions) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Helper to update organization's CRM connection status via Remi API
  const updateOrgCrmStatus = async (
    orgId: string,
    appKey: string,
    connected: boolean,
    subdomain?: string
  ) => {
    const crmKey = normalizeAppKey(appKey);
    const existingRollout = selectedOrg?.rollout || {};
    const existingCrm =
      ((existingRollout as Record<string, unknown>)[crmKey] as Record<string, unknown>) || {};

    const crmUpdate = connected
      ? {
          ...existingCrm,
          connected: true,
          connectedAt: new Date().toISOString(),
          ...(subdomain ? { subdomain } : {}),
        }
      : {
          connected: false,
          disconnectedAt: new Date().toISOString(),
        };

    await updateOrganization(orgId, {
      rolloutConfig: {
        ...existingRollout,
        [crmKey]: crmUpdate,
      },
    });
  };

  // CONNECT mutation
  const connectMutation = useMutation({
    mutationFn: async ({ id, appKey, subdomain }: CredentialPayload) => {
      if (!selectedOrg?.id) throw new Error('Organization ID is required');

      // Notify backend
      await notifyCredentialConnected({
        credential_id: id,
        app: appKey,
        organization_id: selectedOrg.id,
      });

      // Update CRM connection state via Remi API
      await updateOrgCrmStatus(selectedOrg.id, appKey, true, subdomain);

      return { id, appKey };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      const displayName = getAppDisplayName(data.appKey);
      showToast({
        message: `${displayName} connected successfully!`,
        variant: 'success',
      });

      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error connecting credential:', error);
      showToast({
        message: 'Failed to connect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // DISCONNECT mutation
  const disconnectMutation = useMutation({
    mutationFn: async ({ id, appKey }: CredentialPayload) => {
      if (!selectedOrg?.id) throw new Error('Organization ID is required');

      // Notify backend
      await notifyCredentialDeleted({
        credential_id: id,
        organization_id: selectedOrg.id,
      });

      // Update CRM disconnection state via Remi API
      await updateOrgCrmStatus(selectedOrg.id, appKey, false);

      return { id, appKey };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      const displayName = getAppDisplayName(data.appKey);
      showToast({
        message: `${displayName} disconnected successfully`,
        variant: 'info',
      });
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error disconnecting credential:', error);
      showToast({
        message: 'Failed to disconnect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // Handlers to be passed to CredentialsManager callbacks
  const handleCredentialAdded = async (credential: CredentialPayload) => {
    console.log('Credential added:', credential.id, credential.appKey);
    await connectMutation.mutateAsync(credential);
  };

  const handleCredentialDeleted = async (credential: CredentialPayload) => {
    console.log('Credential deleted:', credential.id, credential.appKey);
    await disconnectMutation.mutateAsync(credential);
  };

  return {
    /** Handler for CredentialsManager onCredentialAdded callback */
    handleCredentialAdded,
    /** Handler for CredentialsManager onCredentialDeleted callback */
    handleCredentialDeleted,
    /** Whether a connection is in progress */
    isConnecting: connectMutation.isPending,
    /** Whether a disconnection is in progress */
    isDisconnecting: disconnectMutation.isPending,
    /** Connection error if any */
    connectError: connectMutation.error,
    /** Disconnection error if any */
    disconnectError: disconnectMutation.error,
  };
};
